// src/pages/seeker/SeekerSkills.jsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import { useConfirm, EditBtn, AddBtn, DelBtn } from '../../components/shared/UI';

const C = {
  blue:       '#3D80CC',
  white:      '#ffffff',
  offwhite:   '#f4f7fb',
  border:     '#d0daea',
  muted:      '#666',
  pillText:   '#2c3e6b',
};

const SYSTEM_SECTIONS = [
  { systemKey: 'GENERAL',     title: 'General Skills' },
  { systemKey: 'SOFT_SKILLS', title: 'Soft Skills' },
  { systemKey: 'SOFTWARE',    title: 'Software & Applications' },
  { systemKey: 'HARDWARE',    title: 'Hardware & Equipment' },
];

// ── Skill pill colour coding ──────────────────────────────

const EDUCATION_HIERARCHY = {
  'High School Certificate': 1, 'Diploma': 2, 'Advanced Diploma': 3,
  'Graduate Certificate': 4, 'Graduate Diploma': 4,
  'Bachelor Degree': 5, 'Master Degree': 6, 'Doctorate': 7,
};

const getExpColor = (years) => {
  if (years >= 8) return { bg: '#4C0099', color: '#E6E6E6', border: '#3a0075' };
  if (years >= 5) return { bg: '#7F00FF', color: '#E6E6E6', border: '#6600cc' };
  if (years >= 3) return { bg: '#B266FF', color: '#E6E6E6', border: '#9933ff' };
  if (years >= 1) return { bg: '#CC99FF', color: C.pillText, border: '#b380e6' };
  return { bg: '#E5CCFF', color: C.pillText, border: '#d1a3ff' };
};

const getEduColor = (level) => {
  if (level >= 7) return { bg: '#808080', color: '#FFFFFF', border: '#666666' };
  if (level >= 6) return { bg: '#B3B3B3', color: '#FFFFFF', border: '#999999' };
  if (level >= 4) return { bg: '#CCCCCC', color: '#333333', border: '#aaaaaa' };
  if (level >= 2) return { bg: '#E6E6E6', color: '#333333', border: '#cccccc' };
  return { bg: '#FFFFFF', color: '#333333', border: '#cccccc' };
};

const MANUAL_COLOR  = { bg: '#d5e8d4', color: C.pillText, border: '#82b366' };
const DEFAULT_COLOR = { bg: '#e8edf4', color: C.pillText, border: '#c5d0e0' };

// Source types for grouping
const SRC_EXP    = 'experience';
const SRC_EDU    = 'education';
const SRC_MANUAL = 'manual';

/**
 * Build colour map and source map from derivation links.
 * Returns { colorMap: Map<jssId, {bg,color,border}>, sourceMap: Map<jssId, string> }
 */
const buildSkillMaps = (experienceLinks, educationLinks, expRecords, eduRecords) => {
  const colorMap  = new Map();
  const sourceMap = new Map();
  const expById = new Map(expRecords.map(e => [e.id, e]));
  const eduById = new Map(eduRecords.map(e => [e.id, e]));
  const now = new Date();

  // Per-skill experience years
  const yearsMap   = new Map();
  const hasExpLink = new Set();
  for (const link of experienceLinks) {
    const id = link.jobSeekerSkillId;
    hasExpLink.add(id);
    const exp = expById.get(link.workExperienceId);
    if (!exp?.startDate) continue;
    const start = new Date(exp.startDate);
    const end   = exp.isCurrent || !exp.endDate ? now : new Date(exp.endDate);
    const years = Math.max(0, (end - start) / (1000 * 60 * 60 * 24 * 365.25));
    yearsMap.set(id, (yearsMap.get(id) || 0) + years);
  }

  // Per-skill education level
  const eduLevelMap = new Map();
  const hasEduLink  = new Set();
  for (const link of educationLinks) {
    const id = link.jobSeekerSkillId;
    hasEduLink.add(id);
    const edu = eduById.get(link.educationId);
    if (!edu?.degree) continue;
    const level = EDUCATION_HIERARCHY[edu.degree] || 0;
    if (level > (eduLevelMap.get(id) || 0)) eduLevelMap.set(id, level);
  }

  // Assign: experience (purple) > education (grey)
  const allIds = new Set([...hasExpLink, ...hasEduLink]);
  for (const id of allIds) {
    if (hasExpLink.has(id)) {
      colorMap.set(id, getExpColor(yearsMap.get(id) || 0));
      sourceMap.set(id, SRC_EXP);
    } else {
      colorMap.set(id, getEduColor(eduLevelMap.get(id) || 0));
      sourceMap.set(id, SRC_EDU);
    }
  }
  // Skills not in either set → manual (assigned during render)

  return { colorMap, sourceMap };
};

// ── Skill pill ─────────────────────────────────────────────
const SkillPill = ({ label, onRemove, bg, color, borderColor, highlight }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 4px 3px 10px', fontSize: '12px', fontWeight: 600,
    background: bg || DEFAULT_COLOR.bg, color: color || DEFAULT_COLOR.color,
    borderRadius: '20px', border: `1px solid ${borderColor || DEFAULT_COLOR.border}`,
    transition: 'box-shadow 0.3s, transform 0.3s',
    ...(highlight ? {
      boxShadow: '0 0 0 3px rgba(204, 0, 0, 0.5), 0 0 12px rgba(204, 0, 0, 0.3)',
      transform: 'scale(1.08)',
    } : {}),
  }}>
    {label}
    <DelBtn onClick={onRemove} size={10} title="Remove" className="ib-sm" />
  </span>
);

// ── Group label ───────────────────────────────────────────
const GroupLabel = ({ children }) => (
  <div style={{
    width: '100%', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    color: '#999', letterSpacing: '0.06em', marginTop: 2,
  }}>{children}</div>
);

// ── Section card — renders as its own section-box ──────────
const SectionCard = ({ section, allSkills, allocatedSkillIds, isSystem, onRename, onDelete, onAddSkill, onRemoveSkill, colorMap, colorsOn }) => {
  const [editing,  setEditing]  = useState(false);
  const [title,    setTitle]    = useState(section.title);
  const [saving,   setSaving]   = useState(false);
  const titleRef = useRef();

  const sectionSkillIds = new Set(section.skills.map(s => s.jobSeekerSkillId));
  const available = allSkills.filter(jss => !sectionSkillIds.has(jss.id) && !allocatedSkillIds.has(jss.id));

  const commitRename = async () => {
    const t = title.trim();
    if (!t || t === section.title) { setTitle(section.title); setEditing(false); return; }
    setSaving(true);
    await onRename(section.id, t);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="section-box" style={{ marginBottom: 20 }}>
      <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {editing ? (
          <input
            ref={titleRef}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setTitle(section.title); setEditing(false); } }}
            onBlur={commitRename}
            disabled={saving}
            autoFocus
            style={{
              flex: 1, fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4,
              color: 'white', padding: '2px 8px', outline: 'none',
            }}
          />
        ) : (
          <span style={{ flex: 1 }}>{section.title}</span>
        )}
        {!isSystem && !editing && (
          <EditBtn onClick={() => setEditing(true)} title="Rename section" size={12} />
        )}
        {!isSystem && (
          <DelBtn onClick={() => onDelete(section)} title="Delete section" size={12} />
        )}
      </div>

      <div className="section-box-body">
        {section.skills.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {section.skills.map(s => {
              const jssId = s.jobSeekerSkillId;
              const c = colorsOn ? (colorMap.get(jssId) || MANUAL_COLOR) : DEFAULT_COLOR;
              return (
                <SkillPill
                  key={s.id}
                  label={s.jobSeekerSkill?.skill?.displayName || s.displayName || ''}
                  onRemove={() => onRemoveSkill(section.id, jssId)}
                  bg={c.bg} color={c.color} borderColor={c.border}
                />
              );
            })}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', margin: '0 0 10px' }}>
            No skills added to this section yet.
          </p>
        )}

        {available.length > 0 ? (
          <select
            value=""
            onChange={e => { if (e.target.value) onAddSkill(section.id, e.target.value); }}
            style={{
              fontSize: 12, padding: '5px 8px', borderRadius: 5,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.muted, cursor: 'pointer', maxWidth: 260,
            }}
          >
            <option value="">+ Add skill to this section…</option>
            {[...available].sort((a, b) => a.skill.displayName.localeCompare(b.skill.displayName)).map(jss => (
              <option key={jss.id} value={jss.id}>{jss.skill.displayName}</option>
            ))}
          </select>
        ) : (
          <p style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', margin: 0 }}>
            {allSkills.length === 0
              ? 'Add skills to your profile above first.'
              : 'All available skills are already assigned to a section.'}
          </p>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
export const SeekerSkills = () => {
  const { confirm, dialog } = useConfirm();
  const [skills,    setSkills]    = useState([]);
  const [sections,  setSections]  = useState([]);
  const [colorMap,  setColorMap]  = useState(new Map());
  const [sourceMap, setSourceMap] = useState(new Map());
  const [colorsOn,  setColorsOn]  = useState(true);
  const [highlightId, setHighlightId] = useState(null);
  const [loading,   setLoading]   = useState(true);

  // Add-skill input
  const [input,     setInput]     = useState('');
  const [sugg,      setSugg]      = useState([]);
  const [showSug,   setShowSug]   = useState(false);
  const skillInputRef = useRef();
  const highlightTimer = useRef(null);

  // New section form
  const [newTitle,  setNewTitle]  = useState('');
  const [creating,  setCreating]  = useState(false);

  // ── Load ──────────────────────────────────────────────────
  const load = async () => {
    try {
      const [profileRes, derivRes, expRes, eduRes] = await Promise.all([
        seekerAPI.getProfile(),
        seekerAPI.getSkillDerivations(),
        seekerAPI.getExperience(),
        seekerAPI.getEducation(),
      ]);
      setSkills(profileRes.data.data.skills || []);
      const loadedSections = derivRes.data.data.customSections || [];
      setSections(loadedSections);

      const { colorMap: cMap, sourceMap: sMap } = buildSkillMaps(
        derivRes.data.data.experienceLinks || [],
        derivRes.data.data.educationLinks  || [],
        expRes.data.data || [],
        eduRes.data.data || [],
      );
      setColorMap(cMap);
      setSourceMap(sMap);

      // Ensure system sections exist
      for (const sys of SYSTEM_SECTIONS) {
        if (!loadedSections.find(s => s.systemKey === sys.systemKey)) {
          try {
            const res = await seekerAPI.createCustomSection({ title: sys.title, systemKey: sys.systemKey });
            loadedSections.push(res.data.data);
          } catch { /* silently ignore */ }
        }
      }
      setSections([...loadedSections]);
    } catch { toast.error('Failed to load skills'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Cleanup highlight timer on unmount
  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  // ── Autocomplete ──────────────────────────────────────────
  const fetchSugg = async (q) => {
    if (!q.trim()) { setSugg([]); return; }
    try {
      const res = await seekerAPI.searchSkills(q);
      const existing = new Set(skills.map(s => s.skill?.displayName?.toLowerCase()));
      setSugg((res.data.data || []).filter(s => !existing.has(s.displayName?.toLowerCase())));
    } catch { setSugg([]); }
  };

  // ── Highlight duplicate ───────────────────────────────────
  const flashHighlight = useCallback((jssId) => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    setHighlightId(jssId);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 4000);
  }, []);

  // ── Add skill ─────────────────────────────────────────────
  const addSkill = async (displayName) => {
    const name = displayName.trim();
    if (!name) return;
    const existing = skills.find(s => s.skill?.displayName?.toLowerCase() === name.toLowerCase());
    if (existing) {
      toast.error(`"${name}" is already in your skills`);
      flashHighlight(existing.id);
      return;
    }
    setInput(''); setSugg([]); setShowSug(false);
    try {
      const res = await seekerAPI.addSkill({ displayName: name });
      setSkills(prev => [...prev, res.data.data]);
    } catch (err) {
      if (err?.response?.data?.message !== 'Skill already added')
        toast.error(err?.response?.data?.message || 'Failed to save skill');
    }
  };

  // ── Delete skill ──────────────────────────────────────────
  const deleteSkill = async (jss) => {
    if (!await confirm('Remove Skill', `Remove "${jss.skill.displayName}" from your profile?`)) return;
    setSkills(prev => prev.filter(s => s.id !== jss.id));
    setSections(prev => prev.map(sec => ({
      ...sec,
      skills: sec.skills.filter(s => s.jobSeekerSkillId !== jss.id),
    })));
    try { await seekerAPI.removeSkill(jss.skillId); }
    catch { toast.error('Failed to delete skill'); load(); }
  };

  // ── Custom section — create ────────────────────────────────
  const handleCreateSection = async () => {
    const t = newTitle.trim();
    if (!t) return;
    try {
      const res = await seekerAPI.createCustomSection({ title: t });
      setSections(prev => [...prev, { ...res.data.data, skills: [] }]);
      setNewTitle(''); setCreating(false);
    } catch { /* interceptor */ }
  };

  // ── Custom section — rename ────────────────────────────────
  const handleRename = async (sectionId, newName) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, title: newName } : s));
    try { await seekerAPI.updateCustomSection(sectionId, { title: newName }); }
    catch { toast.error('Failed to rename section'); load(); }
  };

  // ── Custom section — delete ────────────────────────────────
  const handleDeleteSection = async (section) => {
    if (!await confirm('Delete Section', `Delete the "${section.title}" section? Skills in your profile will not be removed.`)) return;
    setSections(prev => prev.filter(s => s.id !== section.id));
    try { await seekerAPI.deleteCustomSection(section.id); }
    catch { toast.error('Failed to delete section'); load(); }
  };

  // ── Section — add skill ────────────────────────────────────
  const handleAddSkillToSection = async (sectionId, jobSeekerSkillId) => {
    const jss = skills.find(s => s.id === jobSeekerSkillId);
    if (!jss) return;
    const tempLink = { id: `tmp-${Date.now()}`, jobSeekerSkillId, jobSeekerSkill: jss };
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, skills: [...s.skills, tempLink] } : s));
    try {
      const res = await seekerAPI.addSkillToCustomSection(sectionId, { jobSeekerSkillId });
      setSections(prev => prev.map(s => s.id === sectionId
        ? { ...s, skills: s.skills.map(sk => sk.id === tempLink.id ? { ...res.data.data, jobSeekerSkill: jss } : sk) }
        : s));
    } catch { toast.error('Failed to add skill'); load(); }
  };

  // ── Section — remove skill ─────────────────────────────────
  const handleRemoveSkillFromSection = async (sectionId, jobSeekerSkillId) => {
    setSections(prev => prev.map(s => s.id === sectionId
      ? { ...s, skills: s.skills.filter(sk => sk.jobSeekerSkillId !== jobSeekerSkillId) }
      : s));
    try { await seekerAPI.removeSkillFromCustomSection(sectionId, jobSeekerSkillId); }
    catch { toast.error('Failed to remove skill'); load(); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  const systemSections = sections.filter(s => s.systemKey);
  const customSections = sections.filter(s => !s.systemKey);

  const allocatedSkillIds = new Set(
    sections.flatMap(s => s.skills.map(sk => sk.jobSeekerSkillId))
  );

  const sectionProps = {
    allSkills: skills,
    allocatedSkillIds,
    onRename: handleRename,
    onDelete: handleDeleteSection,
    onAddSkill: handleAddSkillToSection,
    onRemoveSkill: handleRemoveSkillFromSection,
    colorMap,
    colorsOn,
  };

  // ── Group and sort skills for My Skills section ──────────
  const alpha = (a, b) => a.skill.displayName.localeCompare(b.skill.displayName);
  const expSkills    = skills.filter(s => sourceMap.get(s.id) === SRC_EXP).sort(alpha);
  const eduSkills    = skills.filter(s => sourceMap.get(s.id) === SRC_EDU).sort(alpha);
  const manualSkills = skills.filter(s => !sourceMap.has(s.id)).sort(alpha);

  const renderPill = (jss) => {
    const c = colorsOn ? (colorMap.get(jss.id) || MANUAL_COLOR) : DEFAULT_COLOR;
    return (
      <SkillPill key={jss.id} label={jss.skill.displayName} onRemove={() => deleteSkill(jss)}
        bg={c.bg} color={c.color} borderColor={c.border} highlight={highlightId === jss.id} />
    );
  };

  // Autocomplete dropdown rendered via portal
  const inputEl = skillInputRef.current;
  const inputRect = inputEl ? inputEl.getBoundingClientRect() : null;
  const skillDropdown = showSug && sugg.length > 0 && inputRect
    ? ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: inputRect.bottom + 2, left: inputRect.left, width: inputRect.width,
          zIndex: 9999, background: 'white',
          border: '1px solid var(--border)', borderRadius: '3px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '160px', overflowY: 'auto',
        }}>
          {sugg.map(s => (
            <button key={s.id} onMouseDown={() => addSkill(s.displayName)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = C.offwhite}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {s.displayName}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>Skills & Knowledge</h1>
        <p>Manage your skills and organise them into sections for your CVs.</p>
      </div>

      <div className="page-content">

        {/* ── LEGEND ── */}
        <div style={{
          marginBottom: 16, padding: '10px 14px',
          background: '#fafafa', border: '1px solid #ddd', borderRadius: 6, fontSize: 11,
        }}>
          {/* Toggle row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: colorsOn ? 8 : 0 }}>
            <span style={{ fontWeight: 700, textTransform: 'uppercase', color: '#666', fontSize: 11 }}>Skill Colour Legend</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: '#666' }}>
              <span>{colorsOn ? 'On' : 'Off'}</span>
              <span
                onClick={() => setColorsOn(v => !v)}
                style={{
                  display: 'inline-block', width: 34, height: 18, borderRadius: 9,
                  background: colorsOn ? C.blue : '#ccc', position: 'relative',
                  transition: 'background 0.2s', cursor: 'pointer',
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: colorsOn ? 18 : 2,
                  width: 14, height: 14, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </label>
          </div>

          {/* Legend content */}
          {colorsOn && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', color: '#666' }}>Years Experience:</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[
                    { label: '< 1',    ...getExpColor(0) },
                    { label: '1 to 3', ...getExpColor(1) },
                    { label: '3 to 5', ...getExpColor(3) },
                    { label: '5 to 8', ...getExpColor(5) },
                    { label: '8+',     ...getExpColor(8) },
                  ].map(s => (
                    <span key={s.label} style={{
                      padding: '3px 8px', borderRadius: 12, fontWeight: 600,
                      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                    }}>{s.label}</span>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', color: '#666' }}>Education:</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[
                    { label: 'High School', ...getEduColor(1) },
                    { label: 'Diploma',     ...getEduColor(2) },
                    { label: 'Degree',      ...getEduColor(5) },
                    { label: 'Masters',     ...getEduColor(6) },
                    { label: 'Doctorate',   ...getEduColor(7) },
                  ].map(s => (
                    <span key={s.label} style={{
                      padding: '3px 8px', borderRadius: 12, fontWeight: 600,
                      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                    }}>{s.label}</span>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', color: '#666' }}>Other:</div>
                <span style={{
                  padding: '3px 8px', borderRadius: 12, fontWeight: 600,
                  background: MANUAL_COLOR.bg, color: MANUAL_COLOR.color, border: `1px solid ${MANUAL_COLOR.border}`,
                }}>Manual Entry</span>
              </div>
            </div>
          )}
        </div>

        {/* ── MY SKILLS ── */}
        <div className="section-box" style={{ marginBottom: 20, border: '2px solid #3D80CC' }}>
          <div className="section-box-header" style={{ background: '#3D80CC', fontSize: '14px', letterSpacing: '0.04em' }}>My Skills</div>
          <div className="section-box-body" style={{ background: '#f0f6ff' }}>
            {skills.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {expSkills.length > 0 && (
                  <>
                    <GroupLabel>From Experience</GroupLabel>
                    {expSkills.map(renderPill)}
                  </>
                )}
                {eduSkills.length > 0 && (
                  <>
                    <GroupLabel>From Education</GroupLabel>
                    {eduSkills.map(renderPill)}
                  </>
                )}
                {manualSkills.length > 0 && (
                  <>
                    <GroupLabel>Manual Entry</GroupLabel>
                    {manualSkills.map(renderPill)}
                  </>
                )}
              </div>
            )}
            <div style={{ maxWidth: 360 }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  ref={skillInputRef}
                  value={input}
                  onChange={e => { setInput(e.target.value.slice(0, 64)); fetchSugg(e.target.value); setShowSug(true); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); showSug && sugg.length ? addSkill(sugg[0].displayName) : (input.trim() && addSkill(input.trim())); }
                    if (e.key === 'Escape') setShowSug(false);
                  }}
                  onFocus={() => { if (input.trim()) setShowSug(true); }}
                  onBlur={() => setTimeout(() => setShowSug(false), 150)}
                  placeholder="Search or type a skill…"
                  className="form-input"
                  style={{ fontSize: '12px', padding: '6px 10px' }}
                />
                <button onClick={() => input.trim() && addSkill(input.trim())} className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '4px 10px' }}>
                  + Add
                </button>
              </div>
            </div>
            {skillDropdown}
          </div>
        </div>

        {/* ── SYSTEM SECTIONS ── */}
        {systemSections.map(section => (
          <SectionCard key={section.id} section={section} isSystem {...sectionProps} />
        ))}

        {/* ── CUSTOM SECTIONS ── */}
        {customSections.map(section => (
          <SectionCard key={section.id} section={section} {...sectionProps} />
        ))}

        {/* New section form / button */}
        {creating ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              autoFocus
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateSection(); if (e.key === 'Escape') { setCreating(false); setNewTitle(''); } }}
              placeholder="Section name e.g. Technical Skills, Certifications…"
              className="form-input"
              style={{ flex: 1, fontSize: 13 }}
            />
            <button onClick={handleCreateSection} disabled={!newTitle.trim()} className="btn btn-success btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={13} /> Create
            </button>
            <button onClick={() => { setCreating(false); setNewTitle(''); }} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          </div>
        ) : (
          <AddBtn onClick={() => setCreating(true)} size={13} title="New Skill Section" />
        )}

      </div>
    </div>
  );
};
